// React related imports
import { useState, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

// Contexts and Hooks
import { useCatalog } from '../../services/api/catalog/useCatalog';
import { addCatalog } from '../../services/api/catalog/catalog';
import { AddCatalogApiResponse } from '../../services/api/catalog/catalog.types';

// Components
import TinyEditor from '../../components/TinyEditor';
import Layout from '../../components/layout';
import TemplateFields from './TemplateFields';
import { Header, Button } from '@GxT-Team/gxt-components';

// Helpers
import { notifyError, notifySuccess } from '../../helpers/utils';
import { extractCategory, getDocumentNum } from '../../helpers/catalogHelpers';

// Types
import { DocumentTabs, EditorRef, EditorTypeEnum } from '../../types';

// Constants
import { initialValues } from './utils/constants';

// Others
import { zodResolver } from '@hookform/resolvers/zod';
import { templateUploadSchema } from './utils/schemas';
import { generateCatalogTemplatesIdsList } from './utils/helpers';
import Loading from '../../components/Loading';
import { CatalogContext } from '../../contexts/CatalogContext';
import { TemplateFieldsResponse } from './types';

const CatalogTemplateForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [templateVariables, setTemplateVariables] = useState<TemplateFieldsResponse>({
    templateVariables: [],
    projectPlaceholders: [],
  });
  const tinyEditorRef = useRef<EditorRef>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { external = false } = location.state || {};

  const { convertBlobToBase64Query } = useCatalog();
  const queryClient = useQueryClient();

  const { setCatalogFilesUploadStatus } = useContext(CatalogContext);

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    getValues,
    setValue,
  } = useForm({
    resolver: zodResolver(templateUploadSchema),
    mode: 'onChange',
  });

  const isTemplateValidated = isValid && htmlContent && !isLoading && files.length > 0;

  const navigateToSourceDocuments = () => {
    navigate('/list', {
      state: {
        activeTab: DocumentTabs.TARGET,
        templateType: {
          title: `${external ? 'External Templates' : 'Manual Templates'}`,
          value: `${external ? 'External' : 'Target'}`,
        },
      },
    });
  };

  const addTemplateToCatalog = async () => {
    const data = getValues();

    if (!isTemplateValidated) return;

    setIsLoading(true);

    const body = tinyEditorRef.current?.getBody() as HTMLBodyElement;
    const editorContent = body.innerHTML;

    if (
      !editorContent ||
      editorContent.replace(/<[^>]*>/g, '').trim() === '' ||
      body?.textContent?.trim() === ''
    ) {
      notifyError('Missing content in editor');
      setIsLoading(false);
      return;
    }

    const html = await convertBlobToBase64Query.mutateAsync(editorContent);

    if (!html) {
      notifyError('Missing content in editor');
      return;
    }

    setCatalogFilesUploadStatus((prev) => [
      ...prev,
      { id: files[0].name, status: 'pending', title: files[0].name },
    ]);

    const catalogData = {
      ...initialValues,
      documentNum: getDocumentNum(data.templateName),
      title: data.templateName,
      documentName: data.templateName,
      documentCat: extractCategory(data.templateName),
      originalName: data.templateName,
      version: +data.version,
      ctdSectionIds: generateCatalogTemplatesIdsList(data.ctdSections),
      regions: generateCatalogTemplatesIdsList(data.regions),
      documentType: external ? DocumentTabs.EXTERNAL : initialValues.documentType,
      value: html
        .replace(/<</g, '&lt;&lt;')
        .replace(/>>/g, '&gt;&gt;')
        .replace(/<-----/g, '&lt;-----')
        .replace(/----->/g, '-----&gt;'),
      templateVariables: templateVariables.templateVariables,

      // projectPlaceholders: templateVariables.projectPlaceholders,
    };

    const formData = new FormData();

    Object.entries(catalogData).forEach(([key, value]) => {
      if (key === 'templateVariables' && Array.isArray(value)) {
        value.forEach((variable, index) => {
          formData.append(`templateVariables[${index}]`, variable);
        });
      } else {
        formData.append(key, value as string | Blob);
      }
    });

    formData.append('file', files[0]);

    const fileName = files[0].name;

    try {
      const res = await addCatalog(formData);
      const responseData = res.data as AddCatalogApiResponse & { dvFormMessage?: string };
      setCatalogFilesUploadStatus((prev) =>
        prev.map((status) => (status.id === fileName ? { ...status, status: 'success' } : status)),
      );
      notifySuccess('Template added successfully');
      if (responseData?.dvFormMessage) {
        notifySuccess(`${responseData.dvFormMessage}`);
      }
      queryClient.invalidateQueries({ queryKey: ['Catalog'] });
      navigateToSourceDocuments();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string }; message?: string };
      setCatalogFilesUploadStatus((prev) =>
        prev.map((status) => (status.id === fileName ? { ...status, status: 'error' } : status)),
      );
      notifyError(error?.data?.message || error?.message || 'Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <Layout.Header>
        <div className="flex flex-col items-start justify-center p-6 pb-3 flex-1 self-stretch">
          <div className="flex justify-between items-center self-stretch">
            <Header.Heading className={'!text-primary-300'}>Upload Documents</Header.Heading>
            <Header.Actions>
              <Button
                variant="secondary"
                disabled={!isTemplateValidated}
                onClick={handleSubmit(addTemplateToCatalog)}>
                Add to Catalog
              </Button>
              <Button variant="secondary" onClick={() => navigate('/catalog')}>
                Cancel
              </Button>
            </Header.Actions>
          </div>
        </div>
      </Layout.Header>
      <Layout.Body>
        <div className="flex items-start gap-4 flex-1 self-stretch ">
          <TemplateFields
            control={control}
            errors={errors}
            setValue={setValue}
            external={external}
            files={files}
            setFiles={setFiles}
            setHtmlContent={setHtmlContent}
            setTemplateVariables={setTemplateVariables}
            templateVariables={templateVariables}
          />
          <div className="flex flex-col gap-6 justify-start flex-1 min-h-0 self-stretch w-full md:w-2/3 md:order-last">
            <TinyEditor
              key="target"
              editorType={EditorTypeEnum.Target}
              disabled={!!htmlContent}
              ref={tinyEditorRef}
              height="70vh"
              menuBar={false}
              toolBar={false}
              content={htmlContent}
            />
          </div>
        </div>
      </Layout.Body>
      {isLoading && <Loading backgroundColor="bg-zinc-500" type="spin" />}
    </Layout>
  );
};

export default CatalogTemplateForm;
